import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrderManagementData } from '../../context-api/order-management/OrderManagementDataContext';
import {
  DEFAULT_ORDER_TYPE_VIEW,
  NEXT_DAY_PROMO_DAY_TODAY,
  NEXT_DAY_PROMO_DAY_TOMORROW,
  ORDER_TYPE_NEXT_DAY_PROMOTION,
  ORDER_TYPE_RESTAURANT,
} from './orderManagementConfig';
import {
  ACTIVE_ORDERS_DEFAULT_TIMEZONE,
  formatDateForActiveOrdersApi,
  getNextDayPromoDayKeys,
  getNextDayPromoDefaultDates,
  getRegionalBranchForOrdersApi,
  orderMatchesNextDayPromoDay,
  parseActiveOrdersResponse,
} from './orderManagementUtils';

export function useOrderManagementOrders() {
  const {
    activeOrdersData,
    fetchActiveOrders,
    nextDayPromoOrdersData,
    fetchNextDayPromoStoreOrders,
  } = useOrderManagementData();

  const [allOrders, setAllOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [orderTypeView, setOrderTypeView] = useState(DEFAULT_ORDER_TYPE_VIEW);

  const [deliveryDate, setDeliveryDate] = useState(formatDateForActiveOrdersApi);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('all');
  const [nextDayPromoDayFilter, setNextDayPromoDayFilter] = useState(NEXT_DAY_PROMO_DAY_TODAY);

  const [displayPage, setDisplayPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [expandedId, setExpandedId] = useState(null);

  const isNextDayPromo = orderTypeView === ORDER_TYPE_NEXT_DAY_PROMOTION;
  const loading = isNextDayPromo ? nextDayPromoOrdersData.loading : activeOrdersData.loading;

  const nextDayPromoDayKeys = useMemo(
    () => getNextDayPromoDayKeys(),
    [lastRefreshAt, refreshNonce] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const nextDayPromoDates = useMemo(
    () => getNextDayPromoDefaultDates(),
    [lastRefreshAt, refreshNonce] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const nextDayPromoDateLabel = useMemo(
    () => nextDayPromoDates.join(' · '),
    [nextDayPromoDates]
  );

  const loadRestaurantOrders = useCallback(async () => {
    setLoadError(null);
    const res = await fetchActiveOrders({
      date: deliveryDate,
      regionalBranch: getRegionalBranchForOrdersApi(),
      page: 1,
      limit: 100,
      timeZone: ACTIVE_ORDERS_DEFAULT_TIMEZONE,
    });
    const parsed = parseActiveOrdersResponse(res);
    if (!parsed.ok) {
      setLoadError(parsed.message);
      setAllOrders([]);
      setTotalCount(0);
      return;
    }
    setAllOrders(parsed.orders);
    setTotalCount(parsed.count);
    setLastRefreshAt(Date.now());
    setExpandedId(null);
  }, [deliveryDate, fetchActiveOrders]);

  const loadNextDayPromoOrders = useCallback(async () => {
    setLoadError(null);
    const res = await fetchNextDayPromoStoreOrders({
      date: getNextDayPromoDefaultDates(),
      regionalBranch: getRegionalBranchForOrdersApi(),
      page: 1,
      limit: 100,
      timeZone: ACTIVE_ORDERS_DEFAULT_TIMEZONE,
    });
    const parsed = parseActiveOrdersResponse(res);
    if (!parsed.ok) {
      setLoadError(parsed.message);
      setAllOrders([]);
      setTotalCount(0);
      return;
    }
    setAllOrders(parsed.orders);
    setTotalCount(parsed.count);
    setLastRefreshAt(Date.now());
    setExpandedId(null);
  }, [fetchNextDayPromoStoreOrders]);

  useEffect(() => {
    if (orderTypeView !== ORDER_TYPE_RESTAURANT) return;
    loadRestaurantOrders();
  }, [loadRestaurantOrders, refreshNonce, orderTypeView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNextDayPromo) return;
    loadNextDayPromoOrders();
  }, [loadNextDayPromoOrders, refreshNonce, isNextDayPromo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDisplayPage(0);
    setExpandedId(null);
    setStatusFilter('all');
    setOrderIdSearch('');
    setDeliveryTypeFilter('all');
    setNextDayPromoDayFilter(NEXT_DAY_PROMO_DAY_TODAY);
  }, [orderTypeView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDisplayPage(0);
  }, [statusFilter, orderIdSearch, deliveryTypeFilter, deliveryDate, nextDayPromoDayFilter]);

  const nextDayPromoTabCounts = useMemo(() => {
    const counts = {
      [NEXT_DAY_PROMO_DAY_TODAY]: 0,
      [NEXT_DAY_PROMO_DAY_TOMORROW]: 0,
    };
    allOrders.forEach((o) => {
      if (orderMatchesNextDayPromoDay(o, NEXT_DAY_PROMO_DAY_TODAY, nextDayPromoDayKeys)) {
        counts[NEXT_DAY_PROMO_DAY_TODAY] += 1;
      }
      if (orderMatchesNextDayPromoDay(o, NEXT_DAY_PROMO_DAY_TOMORROW, nextDayPromoDayKeys)) {
        counts[NEXT_DAY_PROMO_DAY_TOMORROW] += 1;
      }
    });
    return counts;
  }, [allOrders, nextDayPromoDayKeys]);

  const applyRestaurantToolbarFilters = (list) => {
    let filtered = list;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((o) => o.internal_order_status === statusFilter);
    }
    if (deliveryTypeFilter !== 'all') {
      filtered = filtered.filter((o) =>
        String(o.delivery_type ?? '').toLowerCase().includes(deliveryTypeFilter)
      );
    }
    if (orderIdSearch.trim()) {
      const q = orderIdSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (o) =>
          String(o.order_no ?? '').toLowerCase().includes(q) ||
          String(o._id ?? '').toLowerCase().includes(q) ||
          `${o.cust_firstName ?? ''} ${o.cust_lastName ?? ''}`.toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  const filteredOrders = useMemo(() => {
    if (isNextDayPromo) {
      let list = allOrders.filter((o) =>
        orderMatchesNextDayPromoDay(o, nextDayPromoDayFilter, nextDayPromoDayKeys)
      );
      if (nextDayPromoDayFilter === NEXT_DAY_PROMO_DAY_TODAY) {
        list = applyRestaurantToolbarFilters(list);
      }
      return list;
    }

    return applyRestaurantToolbarFilters(allOrders);
  }, [
    allOrders,
    isNextDayPromo,
    nextDayPromoDayFilter,
    nextDayPromoDayKeys,
    statusFilter,
    orderIdSearch,
    deliveryTypeFilter,
  ]);

  const displayedRows = filteredOrders.slice(
    displayPage * rowsPerPage,
    displayPage * rowsPerPage + rowsPerPage
  );

  const tabCounts = useMemo(() => {
    const counts = { all: allOrders.length };
    allOrders.forEach((o) => {
      const s = o.internal_order_status;
      if (s) counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [allOrders]);

  const formatLastRefresh = (ts) => {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const toggleRow = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const refresh = () => setRefreshNonce((n) => n + 1);

  return {
    loading,
    totalCount: isNextDayPromo ? allOrders.length : totalCount,
    loadError,
    lastRefreshAt,
    orderTypeView,
    setOrderTypeView,
    deliveryDate,
    setDeliveryDate,
    isNextDayPromo,
    nextDayPromoDateLabel,
    nextDayPromoDayFilter,
    setNextDayPromoDayFilter,
    nextDayPromoTabCounts,
    statusFilter,
    setStatusFilter,
    orderIdSearch,
    setOrderIdSearch,
    deliveryTypeFilter,
    setDeliveryTypeFilter,
    displayPage,
    setDisplayPage,
    rowsPerPage,
    setRowsPerPage,
    expandedId,
    filteredOrders,
    displayedRows,
    tabCounts,
    formatLastRefresh,
    toggleRow,
    refresh,
    setAllOrders,
  };
}
